import React, {FC, ReactNode, useMemo, useState} from 'react';

import {useThemes} from '@/hooks/useThemes';
import {makeTheme} from '@/themes/partialTheme';
import {IColors, IDynamicTheme, ITheme} from '@/types/theme';
import {parseColorsFromUrl} from '@/utils/theme';

import {ThemeContext} from './context';

const defaultThemeName = 'spinamp';

const EmbedThemeProvider: FC<{children: ReactNode}> = ({children}) => {
  const {getTheme, isValidTheme} = useThemes(true);

  const [screenTheme, setScreenTheme] = useState<ITheme | null>(null);

  // prioritize custom theme over predefined if both exists
  const overrideTheme = ({predefinedThemeName, customTheme}: IDynamicTheme) => {
    if (customTheme) {
      setScreenTheme(makeTheme({basic: customTheme}));
    } else if (predefinedThemeName && isValidTheme(predefinedThemeName)) {
      setScreenTheme(getTheme(predefinedThemeName));
    }
  };
  const resetTheme = () => {
    setScreenTheme(null);
  };

  const [customColors, setCustomColors] = useState<Partial<IColors> | null>(
    null,
  );
  const overrideColors = (stringifiedColors: string) => {
    setCustomColors(parseColorsFromUrl(stringifiedColors));
  };
  const resetColors = () => setCustomColors(null);

  const theme = useMemo(() => {
    const baseTheme = screenTheme || getTheme(defaultThemeName);

    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        ...customColors,
      },
    };
  }, [screenTheme, defaultThemeName, customColors, getTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        overrideTheme,
        resetTheme,
        overrideColors,
        resetColors,
      }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default EmbedThemeProvider;
